import ReedyLogo from "assets/svg/reedy-logo.svg?react";
import { Button } from "components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Trans, useTranslation } from "react-i18next";
import { Text } from "components/text";

interface TermsModalProps {
  open: boolean;
  onAgree: () => void;
  onCancel: () => void;
}

interface LoginLinkProps {
  href: string;
  ariaLabel: string;
  children?: React.ReactNode;
}

const LoginLink = ({ href, ariaLabel, children }: LoginLinkProps) => {
  return (
    <a
      className="underline underline-offset-2 hover:text-secondary-800"
      href={href}
      aria-label={ariaLabel}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </a>
  );
};

export function TermsModal({ open, onAgree, onCancel }: TermsModalProps) {
  const { t } = useTranslation("login");

  return (
    <Dialog open={open}>
      {open && (
        <div className="fixed top-2 left-2 z-[1000] text-2xl/4 text-foreground font-normal">
          <ReedyLogo />
        </div>
      )}
      <DialogContent
        showCloseButton={false}
        overlayClassName="z-[999] bg-primary-50"
        className="z-[1000] sm:max-w-[600px] px-8 py-6"
      >
        <DialogHeader className="flex flex-col items-center space-y-4">
          <span className="text-5xl pb-0 mb-0">👋</span>
          <DialogTitle className="text-2xl font-bold mb-0">
            {t("welcome.title")}
          </DialogTitle>
          <DialogDescription className="text-lg font-normal text-secondary-800">
            {t("welcome.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <Text as="div" variant="muted" className="text-center">
          <Trans
            i18nKey="login:terms-agreement"
            components={{
              masterAgreement: (
                <LoginLink
                  href="https://www.reedy.ai/master-agreement"
                  ariaLabel={t("master-agreement")}
                />
              ),
              dataProcessingAgreement: (
                <LoginLink
                  href="https://www.reedy.ai/data-processing-agreement"
                  ariaLabel={t("data-processing-agreement")}
                />
              ),
              termsTrial: (
                <LoginLink
                  href="https://www.reedy.ai/terms-for-the-trial-period"
                  ariaLabel={t("terms-trial")}
                />
              ),
              privacyPolicy: (
                <LoginLink
                  href="https://www.chaptr.com/privacy-policy"
                  ariaLabel={t("privacy-policy")}
                />
              ),
            }}
          />
        </Text>

        <DialogFooter className="justify-center pt-4 gap-3 sm:justify-center">
          <Button
            variant="tertiary"
            onClick={onCancel}
            label={t("buttons.cancel")}
          />
          <Button
            variant="primary"
            onClick={onAgree}
            label={t("welcome.agree-continue")}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
